import { Attachments } from '../components/Attachments';
import { Icon } from '../components/Icon';
import { UserAuditModal } from '../components/UserAuditModal';
import { useCallback, useEffect, useState } from 'react';
import {
  exportEvidence,
  fetchReports,
  preserveEvidence,
  resolveReport,
  StaffError,
  type Report,
} from '../lib/staffClient';

const FILTERS = ['pending', 'resolved', 'dismissed', 'all'] as const;

export function ReportsPage({ onUnauthorized }: { onUnauthorized: () => void }) {
  const [status, setStatus] = useState<(typeof FILTERS)[number]>('pending');
  const [reports, setReports] = useState<Report[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [audit, setAudit] = useState<{ userId: string; tab: 'against' | 'filed' } | null>(null);

  const fail = useCallback(
    (err: unknown) => {
      if (err instanceof StaffError && err.status === 401) onUnauthorized();
      else setError(err instanceof StaffError ? err.message : 'Something went wrong.');
    },
    [onUnauthorized],
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      setReports((await fetchReports(status)).reports);
    } catch (err) {
      fail(err);
    }
  }, [status, fail]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(r: Report, resolution: 'resolved' | 'dismissed', removeContent: boolean) {
    setBusyId(r.id);
    setError(null);
    try {
      await resolveReport(r.id, resolution, removeContent, '');
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusyId(null);
    }
  }

  async function preserve(r: Report) {
    setBusyId(r.id);
    setError(null);
    try {
      await preserveEvidence(r.id);
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusyId(null);
    }
  }

  async function exportIt(r: Report) {
    setBusyId(r.id);
    setError(null);
    try {
      const saved = await exportEvidence(r.id);
      setNotice(`Saved to your Downloads folder: ${saved.join(', ')}`);
    } catch (err) {
      fail(err);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <header className="staff-top">
        <h1 className="skf-title">Reports</h1>
        <div className="staff-actions">
          <select
            aria-label="Report status"
            className="staff-input"
            value={status}
            onChange={(e) => setStatus(e.target.value as (typeof FILTERS)[number])}
          >
            {FILTERS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          <button
            className="btn btn--ghost btn--icon"
            aria-label="Refresh"
            title="Refresh"
            onClick={() => void load()}
          >
            <Icon name="arrow-clockwise" />
          </button>
        </div>
      </header>
      {error && (
        <p className="staff-error" role="alert">
          {error}
        </p>
      )}
      {notice && <p className="skf-meta">{notice}</p>}
      {!reports && !error && <p className="skf-meta">Loading…</p>}
      {reports && reports.length === 0 && <p className="skf-meta">No {status} reports.</p>}
      <div className="staff-list">
        {reports?.map((r) => (
          <article key={r.id} className="staff-tile">
            <p className="staff-tile__label">
              {r.reason.replace('_', ' ')}
              {r.reason === 'illegal_content' && (
                <strong className="staff-flag"> · illegal content</strong>
              )}
              {r.escalated_to_admin && ' · escalated'} · {r.status}
            </p>
            <p className="staff-tile__sub">
              {r.target_type.replace('_', ' ')}
              {r.skiff_title ? ` in “${r.skiff_title}”` : ''} · reported by{' '}
              {r.reporter || 'unknown'}
              {r.reported_user ? ` · about ${r.reported_user}` : ''} ·{' '}
              {new Date(r.created.replace(' ', 'T')).toLocaleString()}
            </p>
            <blockquote className="staff-quote">
              {r.content_snippet === null
                ? 'Content no longer exists.'
                : r.content_snippet || '(no text — media only)'}
              {r.content_removed && <em> — already removed</em>}
            </blockquote>
            <Attachments items={r.attachments} hidden={r.reason === 'illegal_content'} />
            <div className="staff-card-foot">
              <div className="staff-actions">
                {r.evidence_id ? (
                  <button
                    className="btn btn--ghost"
                    disabled={busyId === r.id}
                    onClick={() => void exportIt(r)}
                  >
                    <Icon name="download" /> Export evidence
                  </button>
                ) : (
                  r.content_snippet !== null && (
                    <button
                      className="btn btn--ghost"
                      disabled={busyId === r.id}
                      title="Snapshot the content and files for counsel / law enforcement, then hide it from the app"
                      onClick={() => {
                        if (
                          window.confirm(
                            'Preserve this content as evidence and hide it from the app? This cannot be undone.',
                          )
                        )
                          void preserve(r);
                      }}
                    >
                      <Icon name="safe" /> Preserve &amp; hide
                    </button>
                  )
                )}
                {r.status === 'pending' && (
                  <>
                    {!r.content_removed && r.content_snippet !== null && (
                      <button
                        className="btn btn--danger"
                        disabled={busyId === r.id}
                        onClick={() => void act(r, 'resolved', true)}
                      >
                        <Icon name="trash" /> Remove &amp; resolve
                      </button>
                    )}
                    <button
                      className="btn btn--ghost"
                      disabled={busyId === r.id}
                      onClick={() => void act(r, 'resolved', false)}
                    >
                      <Icon name="check2" /> Resolve
                    </button>
                    <button
                      className="btn btn--ghost"
                      disabled={busyId === r.id}
                      onClick={() => void act(r, 'dismissed', false)}
                    >
                      <Icon name="x-lg" /> Dismiss
                    </button>
                  </>
                )}
              </div>
              <select
                aria-label="More actions"
                className="staff-input"
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'reported' && r.reported_user_id)
                    setAudit({ userId: r.reported_user_id, tab: 'against' });
                  if (v === 'reporter' && r.reporter_id)
                    setAudit({ userId: r.reporter_id, tab: 'filed' });
                }}
              >
                <option value="">More actions…</option>
                <option value="reported" disabled={!r.reported_user_id}>
                  Audit reported user
                </option>
                <option value="reporter" disabled={!r.reporter_id}>
                  Audit reporter
                </option>
              </select>
            </div>
          </article>
        ))}
      </div>
      {audit && (
        <UserAuditModal
          key={audit.userId + audit.tab}
          userId={audit.userId}
          initialTab={audit.tab}
          onClose={() => setAudit(null)}
          onUnauthorized={onUnauthorized}
        />
      )}
    </>
  );
}
